import {apiSlice} from './apiSlice';

/**
 * Endpoints tool Google Search Console (bên trong một Project).
 *
 * Kết nối/OAuth: getAuthUrl (lấy URL consent) → connectGsc (đổi code lấy token + trả
 * danh sách property) → selectProperty (gắn property, khởi động sync nền). getConnection
 * cho trạng thái + tiến độ đồng bộ (poll khi đang backfill).
 *
 * Báo cáo phục vụ TỪ DB (BE không gọi Google real-time): overview (tổng quan + chuỗi
 * thời gian), topQueries, topPages theo khoảng ngày.
 *
 * Tag 'Gsc' theo id `project-<id>` để invalidate khi connect/property/disconnect.
 */
export const gscApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getGscConnection: builder.query({
			query: (projectId) => ({url: `gsc/projects/${projectId}/connection`, method: 'get'}),
			transformResponse: (body) => body?.data || null,
			providesTags: (result, error, projectId) => [{type: 'Gsc', id: `project-${projectId}`}],
		}),
		getGscAuthUrl: builder.mutation({
			query: (projectId) => ({url: `gsc/projects/${projectId}/auth-url`, method: 'get'}),
			transformResponse: (body) => body?.data || null,
		}),
		connectGsc: builder.mutation({
			query: ({projectId, code}) => ({url: `gsc/projects/${projectId}/connect`, method: 'post', data: {code}}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [{type: 'Gsc', id: `project-${projectId}`}],
		}),
		getGscSites: builder.query({
			query: (projectId) => ({url: `gsc/projects/${projectId}/sites`, method: 'get'}),
			transformResponse: (body) => body?.data?.sites || [],
		}),
		selectGscProperty: builder.mutation({
			query: ({projectId, property_url}) => ({url: `gsc/projects/${projectId}/property`, method: 'post', data: {property_url}}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [{type: 'Gsc', id: `project-${projectId}`}],
		}),
		disconnectGsc: builder.mutation({
			query: (projectId) => ({url: `gsc/projects/${projectId}/connection`, method: 'delete'}),
			invalidatesTags: (result, error, projectId) => [{type: 'Gsc', id: `project-${projectId}`}],
		}),
		getGscOverview: builder.query({
			query: ({projectId, from, to}) => ({
				url: `gsc/projects/${projectId}/overview`,
				method: 'get',
				...(from || to ? {params: {...(from ? {from} : {}), ...(to ? {to} : {})}} : {}),
			}),
			transformResponse: (body) => body?.data || null,
			providesTags: (result, error, {projectId}) => [{type: 'Gsc', id: `overview-${projectId}`}],
		}),
		getGscQueries: builder.query({
			query: ({projectId, from, to, limit = 50}) => ({
				url: `gsc/projects/${projectId}/queries`,
				method: 'get',
				params: {...(from ? {from} : {}), ...(to ? {to} : {}), limit},
			}),
			transformResponse: (body) => body?.data?.rows || [],
		}),
		getGscPages: builder.query({
			query: ({projectId, from, to, limit = 50}) => ({
				url: `gsc/projects/${projectId}/pages`,
				method: 'get',
				params: {...(from ? {from} : {}), ...(to ? {to} : {}), limit},
			}),
			transformResponse: (body) => body?.data?.rows || [],
		}),
		getGscCountries: builder.query({
			query: ({projectId, from, to, limit = 50}) => ({
				url: `gsc/projects/${projectId}/countries`,
				method: 'get',
				params: {...(from ? {from} : {}), ...(to ? {to} : {}), limit},
			}),
			transformResponse: (body) => body?.data?.rows || [],
		}),
		getGscSearchTypes: builder.query({
			query: ({projectId, from, to}) => ({
				url: `gsc/projects/${projectId}/searchtypes`,
				method: 'get',
				params: {...(from ? {from} : {}), ...(to ? {to} : {})},
			}),
			transformResponse: (body) => body?.data?.rows || [],
		}),
		getGscContent: builder.query({
			query: ({projectId, from, to, limit = 24}) => ({
				url: `gsc/projects/${projectId}/content`,
				method: 'get',
				params: {...(from ? {from} : {}), ...(to ? {to} : {}), limit},
			}),
			transformResponse: (body) => body?.data || {rows: [], pending: 0},
		}),
		enrichGscContent: builder.mutation({
			query: ({projectId, from, to}) => ({
				url: `gsc/projects/${projectId}/content/enrich`,
				method: 'post',
				data: {...(from ? {from} : {}), ...(to ? {to} : {})},
			}),
			transformResponse: (body) => body?.data || {enriched: 0, pending: 0},
		}),
		// Sitemap v1 chỉ-đọc: list. `refresh` (truthy, đổi mỗi lần bấm "Đồng bộ") → ép BE
		// gọi lại Google (bỏ qua TTL). Mỗi giá trị refresh là 1 cache key mới nên refetch.
		getGscSitemaps: builder.query({
			query: ({projectId, refresh}) => ({
				url: `gsc/projects/${projectId}/sitemaps`,
				method: 'get',
				...(refresh ? {params: {refresh: 1}} : {}),
			}),
			transformResponse: (body) => body?.data || {rows: [], synced_at: null, stale: false},
			providesTags: (result, error, {projectId}) => [{type: 'Gsc', id: `sitemaps-${projectId}`}],
		}),
		// Phân tích nâng cao (thuần DB): quick wins + URL/từ khoá tăng-giảm so kỳ trước.
		getGscAnalysis: builder.query({
			query: ({projectId, from, to}) => ({
				url: `gsc/projects/${projectId}/analysis`,
				method: 'get',
				params: {...(from ? {from} : {}), ...(to ? {to} : {})},
			}),
			transformResponse: (body) => body?.data || null,
		}),
		// Cannibalization (snapshot): từ khoá có nhiều URL cùng đua. Phục vụ từ DB; tự làm mới
		// khi quá hạn hoặc khi `refresh` (đổi mỗi lần bấm "Đồng bộ") → ép BE gọi lại Google.
		getGscCannibalization: builder.query({
			query: ({projectId, refresh}) => ({
				url: `gsc/projects/${projectId}/cannibalization`,
				method: 'get',
				...(refresh ? {params: {refresh: 1}} : {}),
			}),
			transformResponse: (body) => body?.data || {rows: [], synced_at: null, stale: false},
		}),
		// URL Inspection (on-demand): kiểm trạng thái index 1 URL; refresh=1 ép gọi lại Google.
		inspectGscUrl: builder.mutation({
			query: ({projectId, url, refresh}) => ({
				url: `gsc/projects/${projectId}/inspect`,
				method: 'post',
				data: {url, ...(refresh ? {refresh: 1} : {})},
			}),
			transformResponse: (body) => body?.data || null,
		}),
	}),
});

export const {
	useGetGscConnectionQuery,
	useGetGscAuthUrlMutation,
	useConnectGscMutation,
	useGetGscSitesQuery,
	useSelectGscPropertyMutation,
	useDisconnectGscMutation,
	useGetGscOverviewQuery,
	useGetGscQueriesQuery,
	useGetGscPagesQuery,
	useGetGscCountriesQuery,
	useGetGscSearchTypesQuery,
	useGetGscContentQuery,
	useEnrichGscContentMutation,
	useGetGscSitemapsQuery,
	useGetGscAnalysisQuery,
	useInspectGscUrlMutation,
	useGetGscCannibalizationQuery,
} = gscApiSlice;
